import { lazy, Suspense, type ReactNode } from "react";
import { createBrowserRouter } from "react-router-dom";
import { NotFoundPage } from "@/features/content/pages/NotFoundPage";
import { HomePage } from "@/features/home/pages/HomePage";
import { RouteScrollManager } from "@/shared/components/RouteScrollManager";

// Paginas de conteudo sao lazy SOMENTE no router do cliente. O SSR
// (entry-server.tsx) continua importando-as eager para o renderToString
// produzir HTML completo. Na hidratacao, o React preserva o HTML do SSR
// enquanto o chunk carrega (hydration seletiva), entao nao ha flash nem perda
// de conteudo; o fallback so aparece em navegacao client-side. Isso tira o
// react-markdown/remark/rehype do bundle de entrada da home.
const ArticleDetailPage = lazy(() =>
  import("@/features/content/pages/ArticleDetailPage").then((m) => ({
    default: m.ArticleDetailPage,
  })),
);
const ArticlesPage = lazy(() =>
  import("@/features/content/pages/ArticlesPage").then((m) => ({
    default: m.ArticlesPage,
  })),
);
const CaseDetailPage = lazy(() =>
  import("@/features/content/pages/CaseDetailPage").then((m) => ({
    default: m.CaseDetailPage,
  })),
);
const CasesPage = lazy(() =>
  import("@/features/content/pages/CasesPage").then((m) => ({
    default: m.CasesPage,
  })),
);

const ArticleEditorPage = lazy(() =>
  import("@/features/cms/pages/ArticleEditorPage").then((module) => ({
    default: module.ArticleEditorPage,
  })),
);
const CaseEditorPage = lazy(() =>
  import("@/features/cms/pages/CaseEditorPage").then((module) => ({
    default: module.CaseEditorPage,
  })),
);
const CmsAnalyticsPage = lazy(() =>
  import("@/features/cms/pages/CmsAnalyticsPage").then((module) => ({
    default: module.CmsAnalyticsPage,
  })),
);
const CmsContactsPage = lazy(() =>
  import("@/features/cms/pages/CmsContactsPage").then((module) => ({
    default: module.CmsContactsPage,
  })),
);
const CmsContentPage = lazy(() =>
  import("@/features/cms/pages/CmsContentPage").then((module) => ({
    default: module.CmsContentPage,
  })),
);
const CmsSettingsPage = lazy(() =>
  import("@/features/cms/pages/CmsSettingsPage").then((module) => ({
    default: module.CmsSettingsPage,
  })),
);
const CmsTestimonialsPage = lazy(() =>
  import("@/features/cms/pages/CmsTestimonialsPage").then((module) => ({
    default: module.CmsTestimonialsPage,
  })),
);

function lazyRoute(element: ReactNode) {
  return <Suspense fallback={null}>{element}</Suspense>;
}

function publicRoute(element: ReactNode) {
  return (
    <>
      <RouteScrollManager />
      {element}
    </>
  );
}

export const router = createBrowserRouter([
  {
    path: "/",
    element: publicRoute(<HomePage />),
  },
  {
    path: "/pt",
    element: publicRoute(<HomePage />),
  },
  {
    path: "/en",
    element: publicRoute(<HomePage />),
  },
  {
    path: "/artigos",
    element: publicRoute(lazyRoute(<ArticlesPage />)),
  },
  {
    path: "/pt/artigos",
    element: publicRoute(lazyRoute(<ArticlesPage />)),
  },
  {
    path: "/en/artigos",
    element: publicRoute(lazyRoute(<ArticlesPage />)),
  },
  {
    path: "/artigos/:slug",
    element: publicRoute(lazyRoute(<ArticleDetailPage />)),
  },
  {
    path: "/pt/artigos/:slug",
    element: publicRoute(lazyRoute(<ArticleDetailPage />)),
  },
  {
    path: "/en/artigos/:slug",
    element: publicRoute(lazyRoute(<ArticleDetailPage />)),
  },
  {
    path: "/cases",
    element: publicRoute(lazyRoute(<CasesPage />)),
  },
  {
    path: "/pt/cases",
    element: publicRoute(lazyRoute(<CasesPage />)),
  },
  {
    path: "/en/cases",
    element: publicRoute(lazyRoute(<CasesPage />)),
  },
  {
    path: "/cases/:slug",
    element: publicRoute(lazyRoute(<CaseDetailPage />)),
  },
  {
    path: "/pt/cases/:slug",
    element: publicRoute(lazyRoute(<CaseDetailPage />)),
  },
  {
    path: "/en/cases/:slug",
    element: publicRoute(lazyRoute(<CaseDetailPage />)),
  },
  {
    path: "/cms/artigos/novo",
    element: lazyRoute(<ArticleEditorPage />),
  },
  {
    path: "/cms/artigos/:id",
    element: lazyRoute(<ArticleEditorPage />),
  },
  {
    path: "/cms/cases/novo",
    element: lazyRoute(<CaseEditorPage />),
  },
  {
    path: "/cms/cases/:id",
    element: lazyRoute(<CaseEditorPage />),
  },
  {
    path: "/cms/contatos",
    element: lazyRoute(<CmsContactsPage />),
  },
  {
    path: "/cms/metricas",
    element: lazyRoute(<CmsAnalyticsPage />),
  },
  {
    path: "/cms/configuracoes",
    element: lazyRoute(<CmsSettingsPage />),
  },
  {
    path: "/cms/depoimentos",
    element: lazyRoute(<CmsTestimonialsPage />),
  },
  {
    path: "/cms",
    element: lazyRoute(<CmsContentPage />),
  },
  {
    path: "/cms/*",
    element: lazyRoute(<CmsContentPage />),
  },
  {
    path: "*",
    element: publicRoute(<NotFoundPage />),
  },
]);
